<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { getFirebaseAuth } from '@/config/firebase';
import { getWsClient } from '@/services/WebSocketService';
import { getAuthErrorMessage, useAuthStore } from '@/stores/auth';
import {
  resolveInvitation,
  acceptInvitation,
  requestEmailVerification,
  OrganisationInvitationStatus,
  type OrganisationInvitationPublic,
} from '@/api';
import { extractErrorCode, extractErrorDetail, gateErrorMessage } from '@/utils/errorHandler';
import { recaptchaHeaders } from '@/utils/recaptcha';
import { logger } from '@/utils/logger';

import GaiaAuthCard from '@/components/design/domain/auth/GaiaAuthCard.vue';
import GaiaAuthStatus from '@/components/design/domain/auth/GaiaAuthStatus.vue';
import GaiaGoogleButton from '@/components/design/domain/auth/GaiaGoogleButton.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDivider from '@/components/design/base/GaiaDivider.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaLink from '@/components/design/base/GaiaLink.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaPasswordInput from '@/components/design/base/inputs/GaiaPasswordInput.vue';

const INVITE_TOKEN_STORAGE_KEY = 'gaia-invite-token';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

type PageState =
  'loading' | 'invalid' | 'expired' | 'revoked' | 'accepted-already' | 'gate-blocked' | 'ready';

const pageState = ref<PageState>('loading');
const invitation = ref<OrganisationInvitationPublic | null>(null);
const token = ref<string | null>(null);

const error = ref<string | null>(null);
const info = ref<string | null>(null);
const loading = ref(false);
const accepting = ref(false);

// Password form: 'create' for new accounts, flipped to 'signin' when the
// invited email already has a Firebase account.
const passwordMode = ref<'create' | 'signin'>('create');
const password = ref('');
const sendingReset = ref(false);

// Whether a verification mail is known to be on its way. The create-account
// path requests one; an existing unverified account signing in has not been
// sent anything yet and gets a "Send verification link" button instead.
const verificationMailSent = ref(false);

// Bumped after auth actions whose effects Vue cannot observe (Firebase
// mutates the User object in place, e.g. emailVerified after reload()).
const authRevision = ref(0);

// The invitation flow deliberately branches on the Firebase session only —
// backendAuthState 'unprovisioned' is the *expected* state for invitees.
const authSubstate = computed(
  (): 'anonymous' | 'wrong-account' | 'needs-verification' | 'can-accept' => {
    void authRevision.value;
    const user = authStore.user;
    if (!user) return 'anonymous';
    if (user.email?.toLowerCase() !== invitation.value?.invited_email.toLowerCase()) {
      return 'wrong-account';
    }
    if (!user.emailVerified) return 'needs-verification';
    return 'can-accept';
  }
);

onMounted(async (): Promise<void> => {
  const queryToken = typeof route.query.token === 'string' ? route.query.token : null;
  // localStorage on purpose: the verification mail opens in a NEW tab, which
  // shares the Firebase session but not sessionStorage — VerifyEmailView
  // reads this key to route the invitee back here instead of into the
  // self-serve signup flow.
  token.value = queryToken ?? localStorage.getItem(INVITE_TOKEN_STORAGE_KEY);
  if (!token.value) {
    pageState.value = 'invalid';
    return;
  }
  localStorage.setItem(INVITE_TOKEN_STORAGE_KEY, token.value);
  await resolve();
});

async function resolve(): Promise<void> {
  if (!token.value) return;
  pageState.value = 'loading';

  try {
    const {
      data,
      error: apiError,
      response,
    } = await resolveInvitation({
      path: { token: token.value },
      headers: await recaptchaHeaders('invitation_resolve'),
      throwOnError: false,
    });

    if (apiError) {
      const gateMessage = gateErrorMessage(response, apiError);
      if (gateMessage) {
        pageState.value = 'gate-blocked';
        error.value = gateMessage;
        return;
      }
      const code = extractErrorCode(apiError);
      if (code === 'invitation_expired') {
        pageState.value = 'expired';
      } else if (code === 'invitation_revoked') {
        pageState.value = 'revoked';
      } else {
        pageState.value = 'invalid';
      }
      // A dead invitation must not keep hijacking post-verification routing.
      localStorage.removeItem(INVITE_TOKEN_STORAGE_KEY);
      return;
    }

    invitation.value = data;
    switch (data.status) {
      case OrganisationInvitationStatus.ACCEPTED: {
        pageState.value = 'accepted-already';
        localStorage.removeItem(INVITE_TOKEN_STORAGE_KEY);

        break;
      }
      case OrganisationInvitationStatus.EXPIRED: {
        pageState.value = 'expired';
        localStorage.removeItem(INVITE_TOKEN_STORAGE_KEY);

        break;
      }
      case OrganisationInvitationStatus.REVOKED: {
        pageState.value = 'revoked';
        localStorage.removeItem(INVITE_TOKEN_STORAGE_KEY);

        break;
      }
      default: {
        pageState.value = 'ready';
        // Arriving via VerifyEmailView's post-verification redirect — pick the
        // flow back up unprompted.
        if ('verified' in route.query && authStore.user) {
          void handleConfirmVerification();
        }
      }
    }
  } catch (error_: unknown) {
    logger.error('Error resolving invitation:', error_);
    pageState.value = 'invalid';
  }
}

async function handleAccept(): Promise<void> {
  if (!token.value) return;

  accepting.value = true;
  error.value = null;
  info.value = null;
  // Joining can move the active organisation (a user without one lands in it),
  // which the socket is bound to: close it first so the server's 4001 isn't
  // taken for a change from elsewhere, and reconnect once settled.
  const wsClient = getWsClient();
  const wasConnected = wsClient.disconnectForOrgChange();
  try {
    const { error: apiError } = await acceptInvitation({
      path: { token: token.value },
      throwOnError: false,
    });

    if (apiError) {
      const code = extractErrorCode(apiError);
      switch (code) {
        case 'invitation_expired': {
          pageState.value = 'expired';

          break;
        }
        case 'invitation_revoked': {
          pageState.value = 'revoked';

          break;
        }
        case 'invitation_not_found': {
          pageState.value = 'invalid';

          break;
        }
        case 'email_unverified': {
          authRevision.value++;
          error.value = 'Please verify your email before accepting the invitation.';

          break;
        }
        default: {
          logger.error('Error accepting invitation:', apiError);
          error.value = extractErrorDetail(apiError, 'Could not accept the invitation.');
        }
      }
      return;
    }

    localStorage.removeItem(INVITE_TOKEN_STORAGE_KEY);
    const currentUser = getFirebaseAuth().currentUser;
    if (currentUser) {
      await authStore.validateWithBackend(currentUser);
    }
    // Home points the user at the workspace switcher: an existing member of
    // another organisation is not switched into the one they just joined.
    void router.replace({
      path: '/',
      query: { joined: invitation.value?.organisation_name ?? '' },
    });
  } catch (error_: unknown) {
    logger.error('Error accepting invitation:', error_);
    error.value = 'Could not accept the invitation. Please try again.';
  } finally {
    accepting.value = false;
    if (wasConnected) void wsClient.openConnection();
  }
}

// Accept without a further click only right after an interactive step in this
// session (Google popup / verify confirmation) — for an already-logged-in
// visitor the explicit "Join" click is the consent.
async function maybeAutoAccept(): Promise<void> {
  if (pageState.value === 'ready' && authSubstate.value === 'can-accept') {
    await handleAccept();
  }
}

async function handleGoogleSignIn(): Promise<void> {
  loading.value = true;
  error.value = null;
  info.value = null;
  try {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ login_hint: invitation.value?.invited_email ?? '' });
    await signInWithPopup(getFirebaseAuth(), provider);
    authRevision.value++;
    await maybeAutoAccept();
  } catch (error_: unknown) {
    logger.error('Google sign-in error:', error_);
    if (error_ instanceof FirebaseError) {
      error.value = getAuthErrorMessage(error_.code);
    } else {
      error.value = 'Error during Google sign-in. Please try again.';
    }
  } finally {
    loading.value = false;
  }
}

async function handlePasswordSubmit(): Promise<void> {
  if (!invitation.value) return;
  if (password.value.length < 6) {
    error.value = 'Password must be at least 6 characters long';
    return;
  }

  loading.value = true;
  error.value = null;
  info.value = null;
  try {
    if (passwordMode.value === 'create') {
      await createUserWithEmailAndPassword(
        getFirebaseAuth(),
        invitation.value.invited_email,
        password.value
      );
      // The backend sends the verification mail. Best-effort: the
      // verification panel offers a resend if this fails.
      try {
        const { response } = await requestEmailVerification({
          headers: await recaptchaHeaders('email_verification_request'),
          throwOnError: false,
        });
        if (response?.ok) {
          verificationMailSent.value = true;
          info.value = 'We sent a verification link to your email.';
        }
      } catch (error_: unknown) {
        logger.error('Error requesting verification email:', error_);
      }
      authRevision.value++;
    } else {
      await signInWithEmailAndPassword(
        getFirebaseAuth(),
        invitation.value.invited_email,
        password.value
      );
      authRevision.value++;
      await maybeAutoAccept();
    }
  } catch (error_: unknown) {
    logger.error('Password sign-in error:', error_);
    if (error_ instanceof FirebaseError && error_.code === 'auth/email-already-in-use') {
      passwordMode.value = 'signin';
      error.value = 'An account for this email already exists. Enter its password to sign in.';
    } else if (error_ instanceof FirebaseError) {
      error.value = getAuthErrorMessage(error_.code);
    } else {
      error.value = 'Something went wrong. Please try again.';
    }
  } finally {
    loading.value = false;
  }
}

function togglePasswordMode(): void {
  passwordMode.value = passwordMode.value === 'create' ? 'signin' : 'create';
  password.value = '';
  error.value = null;
  info.value = null;
}

async function handleSendPasswordReset(): Promise<void> {
  if (!invitation.value) return;
  sendingReset.value = true;
  try {
    await sendPasswordResetEmail(getFirebaseAuth(), invitation.value.invited_email);
    error.value = null;
    info.value = 'Password reset email sent. Please check your inbox.';
  } catch (error_: unknown) {
    logger.error('Password reset error:', error_);
    error.value = 'Could not send the password reset email. Please try again.';
  } finally {
    sendingReset.value = false;
  }
}

async function handleResendVerification(): Promise<void> {
  if (!getFirebaseAuth().currentUser) return;
  loading.value = true;
  error.value = null;
  info.value = null;
  try {
    const { error: apiError, response } = await requestEmailVerification({
      headers: await recaptchaHeaders('email_verification_request'),
      throwOnError: false,
    });
    if (response?.ok) {
      verificationMailSent.value = true;
      info.value = 'Verification email sent. Please check your inbox.';
    } else {
      error.value =
        gateErrorMessage(response, apiError) ??
        'Could not send the verification email. Please try again in a moment.';
    }
  } catch (error_: unknown) {
    logger.error('Error requesting verification email:', error_);
    error.value = 'Could not send the verification email. Please try again in a moment.';
  } finally {
    loading.value = false;
  }
}

async function handleConfirmVerification(): Promise<void> {
  loading.value = true;
  error.value = null;
  info.value = null;
  try {
    await authStore.refreshBackendValidation();
    authRevision.value++;
    if (authSubstate.value === 'needs-verification') {
      error.value =
        'Your email is still not verified. Please click the link in the email we sent you.';
    } else {
      await maybeAutoAccept();
    }
  } finally {
    loading.value = false;
  }
}

async function handleSignOut(): Promise<void> {
  await authStore.logout();
  authRevision.value++;
}
</script>

<template>
  <gaia-auth-card v-if="pageState !== 'ready'" width="440px">
    <gaia-auth-status v-if="pageState === 'loading'" loading>Loading invitation…</gaia-auth-status>

    <gaia-auth-status v-else-if="pageState === 'invalid'" title="Invitation not found">
      <template #icon>
        <svg
          width="19"
          height="19"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        >
          <circle cx="9" cy="9" r="6" />
          <path d="m13.5 13.5 3.5 3.5" />
        </svg>
      </template>
      This link doesn't match any invitation. Check that you opened the full link from your email,
      or ask your administrator to send a new one.
      <template #actions>
        <gaia-button size="large" block @click="router.push('/login')">Go to login</gaia-button>
      </template>
    </gaia-auth-status>

    <gaia-auth-status
      v-else-if="pageState === 'gate-blocked'"
      tone="warning"
      title="Could not load the invitation"
    >
      <template #icon>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M10 3 1.8 17h16.4L10 3Z" />
          <path d="M10 8.6v3.6" />
          <circle cx="10" cy="14.8" r=".4" fill="currentColor" />
        </svg>
      </template>
      {{ error ?? 'Please try again.' }}
      <template #actions>
        <gaia-button type="primary" size="large" block @click="resolve">Try again</gaia-button>
      </template>
    </gaia-auth-status>

    <gaia-auth-status v-else-if="pageState === 'expired'" tone="warning" title="Invitation expired">
      <template #icon>
        <svg
          width="19"
          height="19"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        >
          <circle cx="10" cy="10" r="7" />
          <path d="M10 6v4.4l2.8 1.8" />
        </svg>
      </template>
      This invitation is no longer active. Ask your administrator to send a new one.
      <template #actions>
        <gaia-button size="large" block @click="router.push('/login')">Go to login</gaia-button>
      </template>
    </gaia-auth-status>

    <gaia-auth-status v-else-if="pageState === 'revoked'" tone="alert" title="Invitation revoked">
      <template #icon>
        <svg
          width="19"
          height="19"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        >
          <circle cx="10" cy="10" r="7" />
          <path d="m7 7 6 6M13 7l-6 6" />
        </svg>
      </template>
      This invitation was withdrawn. Contact your administrator if you think this is a mistake.
      <template #actions>
        <gaia-button size="large" block @click="router.push('/login')">Go to login</gaia-button>
      </template>
    </gaia-auth-status>

    <gaia-auth-status
      v-else-if="pageState === 'accepted-already'"
      tone="success"
      title="Invitation already accepted"
    >
      <template #icon>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M4 10.5 8.2 14.5 16 6" />
        </svg>
      </template>
      You've already joined {{ invitation?.organisation_name }}. Log in to continue.
      <template #actions>
        <gaia-button type="primary" size="large" block @click="router.push('/login')">
          Go to login
        </gaia-button>
      </template>
    </gaia-auth-status>
  </gaia-auth-card>

  <!-- ready: a live invitation. Which of the four sub-states shows depends on
       the Firebase session, not on the invitation. Its own card because only
       this branch carries the legal footer — the outcome screens have nothing
       to agree to. -->
  <gaia-auth-card v-else width="440px" title="You've been invited">
    <gaia-text
      size="body"
      tone="tertiary"
      :style="{ display: 'block', textAlign: 'center', marginBottom: '22px', lineHeight: 1.6 }"
    >
      <strong>{{ invitation?.organisation_name }}</strong> invited you ({{
        invitation?.invited_email
      }}).
    </gaia-text>

    <n-flex vertical :size="16">
      <gaia-banner v-if="error" plain type="error">{{ error }}</gaia-banner>
      <gaia-banner v-if="info" plain type="success">{{ info }}</gaia-banner>
    </n-flex>

    <!-- No Firebase session: sign in as the invited email -->
    <template v-if="authSubstate === 'anonymous'">
      <gaia-google-button :disabled="loading" @click="handleGoogleSignIn" />
      <gaia-divider variant="subtle">or</gaia-divider>

      <n-flex vertical :size="16">
        <gaia-form-field label="Email" variant="uppercase">
          <gaia-input
            :value="invitation?.invited_email"
            size="large"
            disabled
            style="width: 100%"
          />
        </gaia-form-field>
        <gaia-form-field
          :label="passwordMode === 'create' ? 'Create a password' : 'Password'"
          variant="uppercase"
          :hint="passwordMode === 'create' ? 'At least 6 characters' : undefined"
        >
          <template v-if="passwordMode === 'signin'" #action>
            <gaia-link :disabled="sendingReset" @click="handleSendPasswordReset">
              Forgot password? Send a reset email
            </gaia-link>
          </template>
          <gaia-password-input
            v-model:value="password"
            size="large"
            placeholder="••••••••"
            :disabled="loading"
            :input-props="{
              autocomplete: passwordMode === 'create' ? 'new-password' : 'current-password',
            }"
            style="width: 100%"
            @keydown.enter="handlePasswordSubmit"
          />
        </gaia-form-field>
      </n-flex>

      <gaia-button
        type="primary"
        size="large"
        block
        :loading="loading"
        :style="{ marginTop: '22px' }"
        @click="handlePasswordSubmit"
      >
        {{ passwordMode === 'create' ? 'Create account' : 'Sign in' }}
      </gaia-button>
      <!-- Firebase cannot tell us up front whether the invited email has an
           account, so let existing users pick sign-in themselves instead of
           having to fail a create first. -->
      <n-flex align="center" justify="center" :size="6" :style="{ marginTop: '14px' }">
        <gaia-text size="dense" tone="tertiary">
          {{ passwordMode === 'create' ? 'Already have an account?' : 'New to Gaia Insights?' }}
        </gaia-text>
        <gaia-link :disabled="loading" @click="togglePasswordMode">
          {{ passwordMode === 'create' ? 'Sign in instead' : 'Create an account' }}
        </gaia-link>
      </n-flex>
    </template>

    <!-- Session under a different email: never accept silently -->
    <template v-else-if="authSubstate === 'wrong-account'">
      <gaia-banner plain type="warning" :style="{ marginBottom: '20px' }">
        Signed in with a different account — you are signed in as
        <strong>{{ authStore.user?.email }}</strong
        >, but this invitation is for <strong>{{ invitation?.invited_email }}</strong
        >.
      </gaia-banner>
      <gaia-button type="primary" size="large" block :loading="loading" @click="handleSignOut">
        Sign out and switch account
      </gaia-button>
    </template>

    <!-- Right account, email not verified yet -->
    <template v-else-if="authSubstate === 'needs-verification'">
      <gaia-text
        size="body"
        tone="tertiary"
        :style="{ display: 'block', textAlign: 'center', marginBottom: '20px', lineHeight: 1.6 }"
      >
        <template v-if="verificationMailSent">
          We sent a verification link to <strong>{{ invitation?.invited_email }}</strong
          >. Open it, then come back here.
        </template>
        <template v-else>
          For security reasons you need to verify
          <strong>{{ invitation?.invited_email }}</strong> before joining.
        </template>
      </gaia-text>

      <!-- Whichever action is still outstanding is the primary one. -->
      <gaia-button
        type="primary"
        size="large"
        block
        :loading="loading"
        @click="verificationMailSent ? handleConfirmVerification() : handleResendVerification()"
      >
        {{ verificationMailSent ? "I've verified my email" : 'Send verification link' }}
      </gaia-button>
      <n-flex justify="center" :style="{ marginTop: '14px' }">
        <gaia-link
          :disabled="loading"
          @click="verificationMailSent ? handleResendVerification() : handleConfirmVerification()"
        >
          {{ verificationMailSent ? 'Resend email' : "I've already verified" }}
        </gaia-link>
      </n-flex>
    </template>

    <!-- Verified session under the invited email -->
    <template v-else>
      <gaia-button type="primary" size="large" block :loading="accepting" @click="handleAccept">
        Join {{ invitation?.organisation_name }}
      </gaia-button>
    </template>

    <template #footer>
      By continuing you agree to our
      <gaia-link href="https://www.gaia.law/tos" target="_blank">terms and conditions</gaia-link>
      and
      <gaia-link href="https://www.gaia.law/privacy" target="_blank">privacy policy</gaia-link>.
    </template>
  </gaia-auth-card>

  <n-flex v-if="authStore.user" justify="center" :style="{ marginTop: '18px' }">
    <gaia-link tone="muted" @click="handleSignOut">Sign out</gaia-link>
  </n-flex>
</template>
